import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';

/**
 * EmptyState — shared "no data" presentation (UIP-11).
 *
 * Generalises the good pattern already used on `MyVisitorsPage` so every list
 * surface shows a consistent, actionable empty state instead of ad-hoc copy.
 *
 * Two variants:
 *   - `default`  — the list is genuinely empty; show a primary action that
 *                  populates it (e.g. "Invite someone").
 *   - `filtered` — the list is empty only because of an active filter; show a
 *                  reset affordance distinct from the populate action so the
 *                  user is not misled into thinking they have no data at all.
 */
export interface EmptyStateAction {
  label: string;
  /** Navigate to a route. Renders the action as a router Link. */
  to?: string;
  /** Click handler. Renders the action as a button. Ignored when `to` is set. */
  onClick?: () => void;
}

interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: EmptyStateAction;
  variant?: 'default' | 'filtered';
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  variant = 'default',
}: EmptyStateProps) {
  return (
    <div className="py-12 text-center" data-slot="empty-state" data-variant={variant}>
      {Icon && <Icon className="size-10 mx-auto mb-3 text-[var(--text-tertiary)]" aria-hidden />}
      <p className="text-sm font-medium text-[var(--text-secondary)]">{title}</p>
      {description && <p className="text-xs text-[var(--text-tertiary)] mt-1">{description}</p>}
      {action && (
        <div className="mt-3">
          {action.to ? (
            <Button variant="link" size="sm" asChild>
              <Link to={action.to}>{action.label}</Link>
            </Button>
          ) : (
            <Button variant="link" size="sm" onClick={action.onClick}>
              {action.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
